/**
 * ============================================================
 * FILM INTRO CONTROLLER
 * Cinematografische intro met scene management
 * ============================================================
 */

class FilmIntroController {
  constructor(container) {
    this.container = container;
    this.scenes = container.querySelectorAll('.film-intro__scene');
    this.currentSceneIndex = 0;
    this.totalScenes = this.scenes.length;
    this.isPlaying = true;
    this.isPaused = false;
    this.sceneTimelines = [];
    
    // Settings from data attributes
    this.transitionType = container.dataset.sceneTransition || 'crossfade';
    this.autoAdvance = container.dataset.autoAdvance !== 'false';
    this.loop = container.dataset.loop === 'true';
    
    // Elements
    this.progressBar = container.querySelector('.film-intro__progress-bar');
    this.skipButton = container.querySelector('.film-intro__skip');
    this.audioElement = document.getElementById('film-intro-audio');
    this.audioToggle = container.querySelector('.film-intro__audio-toggle');
    
    // Audio state
    this.isMuted = true;
    this.audioReady = false;
    
    // Bind methods
    this.nextScene = this.nextScene.bind(this);
    this.skipIntro = this.skipIntro.bind(this);
    this.handleKeyboard = this.handleKeyboard.bind(this);
    this.toggleAudio = this.toggleAudio.bind(this);
    
    this.init();
  }
  
  init() {
    console.log(`🎬 Film Intro: ${this.totalScenes} scenes`);

    // Respect prefers-reduced-motion: skip animations entirely
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.prefersReducedMotion) {
      console.log('🎬 Reduced motion detected — skipping to last scene');
      this.complete();
      return;
    }

    // On mobile: skip directly to the last scene
    this.isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (this.isMobile && this.totalScenes > 1) {
      console.log('📱 Mobile detected — skipping to last scene only');
      this.skipToLastScene();
      return;
    }

    // Preload videos
    this.preloadVideos();

    // Setup skip button
    if (this.skipButton) {
      this.skipButton.addEventListener('click', this.skipIntro);
    }

    // Setup audio
    this.initAudio();

    // Keyboard controls
    document.addEventListener('keydown', this.handleKeyboard);

    // Check for audio overlay first
    this.audioOverlay = document.getElementById('audio-overlay');
    if (this.audioOverlay) {
      this.showAudioOverlay();
    } else {
      // Start first scene immediately
      this.startIntro();
    }
  }

  preloadVideos() {
    // Only preload the first scene's video eagerly
    // Other videos are loaded progressively when the previous scene starts
    const firstScene = this.scenes[0];
    if (firstScene) {
      const firstVideo = firstScene.querySelector('.scene__bg-video');
      if (firstVideo) {
        firstVideo.preload = 'auto';
        firstVideo.pause();
        const startTime = parseFloat(firstVideo.dataset.videoStart) || 0;
        firstVideo.addEventListener('loadedmetadata', () => {
          firstVideo.currentTime = startTime > 0 ? startTime : 0;
        }, { once: true });
        firstVideo.load();
        console.log('🎥 Eager preload:', firstVideo.querySelector('source')?.src);
      }
    }

    // Ensure all other videos stay paused and don't auto-load
    for (let i = 1; i < this.totalScenes; i++) {
      const video = this.scenes[i]?.querySelector('.scene__bg-video');
      if (video) {
        video.preload = 'none';
        video.pause();
      }
    }
  }

  /**
   * Preload the next scene's video so it's ready when needed
   */
  preloadNextVideo(currentIndex) {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= this.totalScenes) return;

    const nextScene = this.scenes[nextIndex];
    if (!nextScene) return;

    const nextVideo = nextScene.querySelector('.scene__bg-video');
    if (nextVideo && nextVideo.preload === 'none') {
      nextVideo.preload = 'auto';
      const startTime = parseFloat(nextVideo.dataset.videoStart) || 0;
      nextVideo.addEventListener('loadedmetadata', () => {
        nextVideo.currentTime = startTime > 0 ? startTime : 0;
      }, { once: true });
      nextVideo.load();
      console.log(`🎥 Lazy preload scene ${nextIndex + 1}:`, nextVideo.querySelector('source')?.src);
    }
  }

  /**
   * Setup video trim: seek to start, loop between start and end points
   */
  setupVideoTrim(video) {
    const startTime = parseFloat(video.dataset.videoStart) || 0;
    const endTime = parseFloat(video.dataset.videoEnd) || 0;

    // Seek to start
    if (startTime > 0) {
      video.currentTime = startTime;
    }

    // If end time is set, loop between start and end
    if (endTime > 0 && endTime > startTime) {
      const onTimeUpdate = () => {
        if (video.currentTime >= endTime) {
          video.currentTime = startTime;
        }
      };
      video.addEventListener('timeupdate', onTimeUpdate);
      // Store reference for cleanup
      video._trimHandler = onTimeUpdate;
    }
  }

  /**
   * Remove trim handler from video
   */
  cleanupVideoTrim(video) {
    if (video._trimHandler) {
      video.removeEventListener('timeupdate', video._trimHandler);
      video._trimHandler = null;
    }
  }
  
  showAudioOverlay() {
    // Pause intro until user makes a choice
    this.isPaused = true;
    
    const enableBtn = document.getElementById('enable-audio-btn');
    const skipBtn = document.getElementById('skip-audio-btn');
    
    if (enableBtn) {
      enableBtn.addEventListener('click', () => {
        this.hideOverlayAndStart(true);
      });
    }
    
    if (skipBtn) {
      skipBtn.addEventListener('click', () => {
        this.hideOverlayAndStart(false);
      });
    }
  }
  
  hideOverlayAndStart(enableAudio) {
    if (this.audioOverlay) {
      this.audioOverlay.classList.add('hidden');
    }
    
    if (enableAudio && this.audioElement) {
      this.audioElement.play().then(() => {
        this.isMuted = false;
        if (this.audioToggle) {
          this.audioToggle.setAttribute('data-muted', 'false');
        }
        // Fade in audio
        gsap.fromTo(this.audioElement, 
          { volume: 0 }, 
          { volume: parseFloat(this.audioElement.dataset.volume) || 0.3, duration: 2 }
        );
        console.log('🎵 Audio enabled');
      }).catch(err => {
        console.log('🎵 Audio blocked:', err);
      });
    }
    
    // Start the intro
    this.isPaused = false;
    this.startIntro();
  }
  
  startIntro() {
    if (this.totalScenes > 0) {
      this.playScene(0);
    } else {
      this.complete();
    }
  }
  
  initAudio() {
    if (!this.audioElement) return;
    
    // Set initial volume
    const volume = parseFloat(this.audioElement.dataset.volume) || 0.3;
    this.audioElement.volume = volume;
    
    // Audio toggle button
    if (this.audioToggle) {
      this.audioToggle.addEventListener('click', this.toggleAudio);
    }
  }
  
  toggleAudio() {
    if (!this.audioElement) return;
    
    if (this.isMuted) {
      // Unmute and play
      this.audioElement.play().then(() => {
        this.isMuted = false;
        if (this.audioToggle) {
          this.audioToggle.setAttribute('data-muted', 'false');
        }
        console.log('🎵 Audio playing');
        
        // Fade in audio
        gsap.fromTo(this.audioElement, 
          { volume: 0 }, 
          { volume: parseFloat(this.audioElement.dataset.volume) || 0.3, duration: 1 }
        );
      }).catch(err => {
        console.log('🎵 Audio blocked:', err);
      });
    } else {
      // Mute with fade out
      gsap.to(this.audioElement, {
        volume: 0,
        duration: 0.5,
        onComplete: () => {
          this.audioElement.pause();
          this.isMuted = true;
          if (this.audioToggle) {
            this.audioToggle.setAttribute('data-muted', 'true');
          }
          console.log('🎵 Audio muted');
        }
      });
    }
  }
  
  fadeOutAudio() {
    if (this.audioElement && !this.isMuted) {
      gsap.to(this.audioElement, {
        volume: 0,
        duration: 2,
        onComplete: () => {
          this.audioElement.pause();
        }
      });
    }
  }
  
  playScene(index) {
    if (index >= this.totalScenes) {
      if (this.loop) {
        this.playScene(0);
      } else {
        this.complete();
      }
      return;
    }
    
    const scene = this.scenes[index];
    const prevScene = index > 0 ? this.scenes[index - 1] : null;
    
    // Scene data
    const duration = parseFloat(scene.dataset.sceneDuration) || 3;
    const delay = parseFloat(scene.dataset.sceneDelay) || 0;
    const transitionIn = scene.dataset.sceneTransitionIn || 'fade';
    const transitionOut = scene.dataset.sceneTransitionOut || 'fade';
    const easing = scene.dataset.sceneEasing || 'power2.inOut';
    const sceneType = scene.dataset.sceneType;
    
    console.log(`🎬 Scene ${index + 1}/${this.totalScenes}: ${sceneType} (${duration}s)`);
    
    // Create timeline for this scene
    const isLastScene = (index === this.totalScenes - 1);
    const tl = gsap.timeline({
      onComplete: () => {
        if (isLastScene) {
          // Last scene: stay visible, just mark complete
          this.complete();
          return;
        }
        if (this.autoAdvance && this.isPlaying) {
          this.nextScene();
        }
      }
    });
    
    // Transition out previous scene
    if (prevScene) {
      tl.add(this.createTransitionOut(prevScene, transitionOut, easing), 0);
    }
    
    // Transition in current scene
    tl.add(this.createTransitionIn(scene, transitionIn, easing), delay);
    
    // Animate scene content - start after video fade-in completes
    tl.add(this.animateSceneContent(scene, sceneType), delay + 1.8);

    // Fade out text content before scene ends (skip on last scene)
    const sceneContent = scene.querySelector('.scene__content');
    if (sceneContent && !isLastScene) {
      tl.to(sceneContent, { opacity: 0, y: -10, duration: 1, ease: 'power2.in' }, delay + duration - 1.5);
    }

    // Background Ken Burns (only for images, not videos)
    const bgImage = scene.querySelector('.scene__bg-image');
    const bgVideo = scene.querySelector('.scene__bg-video');
    if (bgImage && !bgVideo && bgImage.dataset.kenburns === 'true') {
      const scale = parseFloat(bgImage.dataset.kenburnsScale) || 1.1;
      tl.to(bgImage, {
        scale: scale,
        duration: duration + 2,
        ease: 'none'
      }, delay);
    }
    
    // Progress bar
    if (this.progressBar) {
      const totalDuration = this.calculateTotalDuration();
      const elapsed = this.calculateElapsedDuration(index);
      tl.to(this.progressBar, {
        width: `${((elapsed + duration) / totalDuration) * 100}%`,
        duration: duration,
        ease: 'none'
      }, delay);
    }
    
    // Wait for scene duration then continue
    tl.to({}, { duration: duration }, delay);

    // Progressively preload the next scene's video
    this.preloadNextVideo(index);

    // Update scene dots
    this.updateSceneDots(index);

    this.currentSceneIndex = index;
    this.sceneTimelines.push(tl);
  }

  updateSceneDots(currentIndex) {
    const dots = this.container.querySelectorAll('.film-intro__scene-dot');
    dots.forEach((dot, i) => {
      dot.classList.remove('is-active', 'is-complete');
      if (i === currentIndex) {
        dot.classList.add('is-active');
      } else if (i < currentIndex) {
        dot.classList.add('is-complete');
      }
    });
  }
  
  createTransitionIn(scene, type, easing) {
    const tl = gsap.timeline();
    const bg = scene.querySelector('.scene__bg-image');
    const video = scene.querySelector('.scene__bg-video');

    // Reset scene
    gsap.set(scene, { visibility: 'visible' });

    // If there's a video, reset to start, setup trim, and play
    if (video) {
      const startTime = parseFloat(video.dataset.videoStart) || 0;
      video.currentTime = startTime;
      video.playbackRate = 1.15; // Slightly faster playback
      this.setupVideoTrim(video);
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch(err => console.log('Video autoplay blocked:', err));
      }
    }

    switch (type) {
      case 'fade':
        tl.fromTo(scene, { opacity: 0 }, { opacity: 1, duration: 1.5, ease: easing });
        if (video) {
          tl.fromTo(video, { opacity: 0 }, { opacity: 1, duration: 1.5 }, 0);
        } else if (bg) {
          tl.fromTo(bg, { opacity: 0 }, { opacity: 1, duration: 1.5 }, 0);
        }
        break;
        
      case 'slide-up':
        tl.fromTo(scene, { opacity: 0, y: 100 }, { opacity: 1, y: 0, duration: 0.8, ease: easing });
        if (video) tl.fromTo(video, { opacity: 0 }, { opacity: 1, duration: 0.8 }, 0);
        else if (bg) tl.fromTo(bg, { opacity: 0 }, { opacity: 1, duration: 0.8 }, 0);
        break;

      case 'slide-down':
        tl.fromTo(scene, { opacity: 0, y: -100 }, { opacity: 1, y: 0, duration: 0.8, ease: easing });
        if (video) tl.fromTo(video, { opacity: 0 }, { opacity: 1, duration: 0.8 }, 0);
        else if (bg) tl.fromTo(bg, { opacity: 0 }, { opacity: 1, duration: 0.8 }, 0);
        break;

      case 'zoom-in':
        tl.fromTo(scene, { opacity: 0, scale: 1.2 }, { opacity: 1, scale: 1, duration: 1, ease: easing });
        if (video) tl.fromTo(video, { opacity: 0, scale: 1.1 }, { opacity: 1, scale: 1, duration: 1.2 }, 0);
        else if (bg) tl.fromTo(bg, { opacity: 0, scale: 1.3 }, { opacity: 1, scale: 1, duration: 1.2 }, 0);
        break;

      case 'blur-in':
        tl.fromTo(scene, { opacity: 0, filter: 'blur(20px)' }, { opacity: 1, filter: 'blur(0px)', duration: 1, ease: easing });
        if (video) tl.fromTo(video, { opacity: 0 }, { opacity: 1, duration: 1 }, 0);
        else if (bg) tl.fromTo(bg, { opacity: 0 }, { opacity: 1, duration: 1 }, 0);
        break;

      default:
        tl.to(scene, { opacity: 1, duration: 0.5 });
        if (video) tl.to(video, { opacity: 1, duration: 0.8 }, 0);
        else if (bg) tl.to(bg, { opacity: 1, duration: 0.8 }, 0);
    }
    
    return tl;
  }
  
  createTransitionOut(scene, type, easing) {
    const tl = gsap.timeline();
    const video = scene.querySelector('.scene__bg-video');

    // Pause video and cleanup trim handler after fade completes
    if (video) {
      tl.call(() => {
        video.pause();
        this.cleanupVideoTrim(video);
      }, null, 1.5);
    }

    switch (type) {
      case 'fade':
        tl.to(scene, { opacity: 0, duration: 1.5, ease: easing });
        break;
        
      case 'slide-up':
        tl.to(scene, { opacity: 0, y: -50, duration: 0.5, ease: easing });
        break;
        
      case 'slide-down':
        tl.to(scene, { opacity: 0, y: 50, duration: 0.5, ease: easing });
        break;
        
      case 'zoom-out':
        tl.to(scene, { opacity: 0, scale: 0.9, duration: 0.5, ease: easing });
        break;
        
      case 'blur-out':
        tl.to(scene, { opacity: 0, filter: 'blur(10px)', duration: 0.5, ease: easing });
        break;
        
      default:
        tl.to(scene, { opacity: 0, duration: 0.3 });
    }
    
    tl.set(scene, { visibility: 'hidden' });
    
    return tl;
  }
  
  animateSceneContent(scene, type) {
    const tl = gsap.timeline();
    
    switch (type) {
      case 'logo_reveal':
        tl.add(this.animateLogoReveal(scene));
        break;
        
      case 'text_reveal':
        tl.add(this.animateTextReveal(scene));
        break;
        
      case 'quote':
        tl.add(this.animateQuote(scene));
        break;
        
      case 'countdown':
        tl.add(this.animateCountdown(scene));
        break;
        
      case 'image_focus':
        // No content animation for pure image
        const caption = scene.querySelector('.scene__caption');
        if (caption) {
          tl.fromTo(caption, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 }, 1);
        }
        break;
    }
    
    return tl;
  }
  
  animateLogoReveal(scene) {
    const tl = gsap.timeline();

    const preText = scene.querySelector('.scene__pre-text');
    const logo = scene.querySelector('.scene__logo');
    const tagline = scene.querySelector('.scene__tagline');
    const line = scene.querySelector('.scene__line');
    const subtitle = scene.querySelector('.scene__subtitle');

    // Pre text
    if (preText) {
      tl.fromTo(preText, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 }, 0);
    }

    // Logo - fade in as a whole
    if (logo) {
      tl.fromTo(logo, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }, 0.3);
    }

    // Line
    if (line) {
      tl.to(line, { scaleX: 1, duration: 0.6, ease: 'power2.inOut' }, 1.0);
    }

    // Tagline
    if (tagline) {
      tl.fromTo(tagline, { opacity: 0 }, { opacity: 1, duration: 0.5 }, 1.2);
    }

    // Subtitle
    if (subtitle) {
      tl.fromTo(subtitle, { opacity: 0 }, { opacity: 1, duration: 0.4 }, 1.4);
    }

    // CTA Buttons (supports multiple)
    const ctas = scene.querySelectorAll('.scene__cta');
    ctas.forEach((ctaBtn, i) => {
      tl.fromTo(ctaBtn, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }, 1.6 + (i * 0.2));
    });

    return tl;
  }
  
  animateTextReveal(scene) {
    const tl = gsap.timeline();
    
    const heading = scene.querySelector('.scene__heading');
    const body = scene.querySelector('.scene__body');
    const headingAnim = heading?.dataset.animate || 'fade-up';
    
    if (heading) {
      switch (headingAnim) {
        case 'split-lines':
          // Split into lines and animate
          const text = heading.textContent;
          heading.innerHTML = `<span class="line-inner">${text}</span>`;
          tl.fromTo(heading.querySelector('.line-inner'), 
            { y: '100%', opacity: 0 }, 
            { y: '0%', opacity: 1, duration: 0.8, ease: 'power3.out' }
          );
          break;
          
        case 'blur-in':
          tl.fromTo(heading, 
            { opacity: 0, filter: 'blur(15px)' }, 
            { opacity: 1, filter: 'blur(0px)', duration: 1 }
          );
          break;
          
        default:
          tl.fromTo(heading, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6 });
      }
    }
    
    if (body) {
      tl.fromTo(body, { opacity: 0, y: 15 }, { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }, '+=0.8');
    }
    
    return tl;
  }
  
  animateQuote(scene) {
    const tl = gsap.timeline();
    
    const quote = scene.querySelector('.scene__quote');
    const author = scene.querySelector('.scene__quote-author');
    if (quote) {
      tl.fromTo(quote, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8 });
    }
    
    if (author) {
      tl.to(author, { opacity: 1, duration: 0.4 }, '-=0.3');
    }
    
    return tl;
  }
  
  animateCountdown(scene) {
    const tl = gsap.timeline();
    
    const container = scene.querySelector('.scene__countdown');
    const numberEl = scene.querySelector('.scene__countdown-number');
    const progress = scene.querySelector('.scene__countdown-progress');
    const start = parseInt(container?.dataset.start) || 5;
    
    // Animate circle
    if (progress) {
      tl.to(progress, {
        strokeDashoffset: 0,
        duration: start,
        ease: 'linear'
      }, 0);
    }
    
    // Countdown numbers
    for (let i = start; i >= 1; i--) {
      tl.call(() => {
        if (numberEl) numberEl.textContent = i;
      }, null, start - i);
      
      // Pulse animation
      tl.fromTo(numberEl, 
        { scale: 1.2, opacity: 0.5 }, 
        { scale: 1, opacity: 1, duration: 0.3 }, 
        start - i
      );
    }
    
    return tl;
  }
  
  
  nextScene() {
    this.playScene(this.currentSceneIndex + 1);
  }
  
  previousScene() {
    if (this.currentSceneIndex > 0) {
      // Kill current timeline
      if (this.sceneTimelines.length > 0) {
        this.sceneTimelines[this.sceneTimelines.length - 1].kill();
      }
      this.playScene(this.currentSceneIndex - 1);
    }
  }
  
  skipIntro() {
    console.log('⏭️ Skipping intro');
    
    // Kill all timelines
    this.sceneTimelines.forEach(tl => tl.kill());
    
    // Complete immediately
    this.complete();
  }

  /**
   * Skip directly to the last scene (used on mobile)
   * Unlike complete(), this actually plays the last scene with animations
   */
  skipToLastScene() {
    console.log('📱 Starting with last scene only');
    
    // Hide skip button immediately on mobile
    if (this.skipButton) this.skipButton.style.display = 'none';
    if (this.progressBar) this.progressBar.parentElement.style.display = 'none';
    
    // Hide all scenes except the last one
    this.scenes.forEach((scene, i) => {
      if (i < this.totalScenes - 1) {
        scene.style.opacity = '0';
        scene.style.visibility = 'hidden';
      }
    });
    
    // Preload only the last scene's video
    const lastScene = this.scenes[this.totalScenes - 1];
    if (lastScene) {
      const video = lastScene.querySelector('.scene__bg-video');
      if (video) {
        video.preload = 'auto';
        const startTime = parseFloat(video.dataset.videoStart) || 0;
        video.addEventListener('loadedmetadata', () => {
          video.currentTime = startTime > 0 ? startTime : 0;
        }, { once: true });
        video.load();
      }
    }
    
    // Check for audio overlay first
    this.audioOverlay = document.getElementById('audio-overlay');
    if (this.audioOverlay) {
      // On mobile, skip the audio overlay and just start muted
      this.audioOverlay.style.display = 'none';
    }
    
    // Play only the last scene
    this.playScene(this.totalScenes - 1);
  }
  
  pause() {
    this.isPaused = true;
    this.sceneTimelines.forEach(tl => tl.pause());
  }
  
  resume() {
    this.isPaused = false;
    this.sceneTimelines.forEach(tl => tl.resume());
  }
  
  handleKeyboard(e) {
    switch (e.key) {
      case 'Escape':
        this.skipIntro();
        break;
      case ' ':
        e.preventDefault();
        this.isPaused ? this.resume() : this.pause();
        break;
      case 'ArrowRight':
        this.nextScene();
        break;
      case 'ArrowLeft':
        this.previousScene();
        break;
    }
  }
  
  calculateTotalDuration() {
    let total = 0;
    this.scenes.forEach(scene => {
      total += parseFloat(scene.dataset.sceneDuration) || 3;
    });
    return total;
  }
  
  calculateElapsedDuration(upToIndex) {
    let elapsed = 0;
    for (let i = 0; i < upToIndex; i++) {
      elapsed += parseFloat(this.scenes[i].dataset.sceneDuration) || 3;
    }
    return elapsed;
  }
  
  complete() {
    console.log('✅ Film intro complete');
    this.isPlaying = false;

    // Kill ALL active timelines to prevent any delayed animations
    this.sceneTimelines.forEach(tl => tl.kill());
    this.sceneTimelines = [];

    // Force last scene visible with direct style manipulation (bypass GSAP)
    const lastScene = this.scenes[this.totalScenes - 1];
    if (lastScene) {
      lastScene.style.opacity = '1';
      lastScene.style.visibility = 'visible';

      // Force the background video visible and playing
      const lastVideo = lastScene.querySelector('.scene__bg-video');
      if (lastVideo) {
        lastVideo.style.opacity = '1';
        lastVideo.currentTime = 0;
        lastVideo.playbackRate = 1.15; // Slightly faster playback
        lastVideo.play().catch(() => {});
      }

      // Force background image visible too
      const lastBgImage = lastScene.querySelector('.scene__bg-image');
      if (lastBgImage) {
        lastBgImage.style.opacity = '1';
      }

      const lastContent = lastScene.querySelector('.scene__content');
      if (lastContent) {
        lastContent.style.opacity = '1';
        lastContent.style.transform = 'none';
      }

      // Force all animated children visible too
      lastScene.querySelectorAll('.scene__logo, .scene__tagline, .scene__line, .scene__pre-text, .scene__cta').forEach(el => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    }

    // Hide all other scenes and stop their videos
    this.scenes.forEach((scene, i) => {
      if (i < this.totalScenes - 1) {
        scene.style.opacity = '0';
        scene.style.visibility = 'hidden';
        const video = scene.querySelector('.scene__bg-video');
        if (video) {
          video.pause();
          this.cleanupVideoTrim(video);
        }
      }
    });

    // Start ping-pong loop on last scene's video
    if (lastScene) {
      const pingPongVideo = lastScene.querySelector('.scene__bg-video');
      if (pingPongVideo) {
        this.cleanupVideoTrim(pingPongVideo);
        this.setupPingPongLoop(pingPongVideo);
      }
    }

    // Hide skip button and progress bar
    if (this.skipButton) this.skipButton.style.display = 'none';
    if (this.progressBar) this.progressBar.parentElement.style.display = 'none';

    // Fade out audio
    this.fadeOutAudio();

    // Cleanup
    document.removeEventListener('keydown', this.handleKeyboard);

    // Dispatch event
    this.container.dispatchEvent(new CustomEvent('introComplete'));

    // Trigger global scroll system
    if (typeof startScrollSystem === 'function') {
      startScrollSystem();
    }

    // Show scroll hint
    const scrollHint = document.getElementById('scroll-hint');
    if (scrollHint) {
      gsap.to(scrollHint, { opacity: 1, visibility: 'visible', duration: 0.5 });
    }
  }
  
  /**
   * Ping-pong video loop: plays forward, then reverses, then forward again
   * Uses requestAnimationFrame for reverse since HTML5 video doesn't support negative playbackRate
   */
  setupPingPongLoop(video) {
    video.loop = false;
    let reversing = false;
    let rafId = null;
    let prevTimestamp = null;

    const reverseStep = (timestamp) => {
      if (!prevTimestamp) prevTimestamp = timestamp;
      const dt = (timestamp - prevTimestamp) / 1000;
      prevTimestamp = timestamp;

      const newTime = video.currentTime - dt;
      if (newTime <= 0) {
        // Reached beginning, play forward again
        video.currentTime = 0;
        reversing = false;
        prevTimestamp = null;
        video.play();
        return;
      }

      video.currentTime = newTime;
      rafId = requestAnimationFrame(reverseStep);
    };

    const onEnded = () => {
      if (!reversing) {
        reversing = true;
        video.pause();
        prevTimestamp = null;
        rafId = requestAnimationFrame(reverseStep);
      }
    };

    video.addEventListener('ended', onEnded);

    // Store cleanup reference
    video._pingPongCleanup = () => {
      video.removeEventListener('ended', onEnded);
      if (rafId) cancelAnimationFrame(rafId);
    };

    // Make sure the video is playing forward
    video.play().catch(() => {});
  }

  destroy() {
    this.sceneTimelines.forEach(tl => tl.kill());
    document.removeEventListener('keydown', this.handleKeyboard);
    if (this.skipButton) {
      this.skipButton.removeEventListener('click', this.skipIntro);
    }
    // Cleanup ping-pong if active
    const lastScene = this.scenes[this.totalScenes - 1];
    if (lastScene) {
      const video = lastScene.querySelector('.scene__bg-video');
      if (video && video._pingPongCleanup) video._pingPongCleanup();
    }
  }
}

// Initialize when DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const filmIntro = document.getElementById('film-intro');
  if (filmIntro && filmIntro.classList.contains('film-intro')) {
    // Check if it's the new scene-based system
    if (filmIntro.dataset.totalScenes) {
      window.filmIntroController = new FilmIntroController(filmIntro);
    }
  }
});